/**
 * PDF Generator Utility
 * Generates printable documents for AWB that can be saved as PDF
 */

import { AWBDetail } from '@/services/awbService';
import JsBarcode from 'jsbarcode';

// Country lookup map type
export interface CountryLookup {
  [id: string]: string; // id -> name
}

// Types for document generation
interface AddressLabelData {
  awbNumber: string;
  parcelNo: string;
  totalParcels: number;
  shipper: {
    name: string;
    address: string;
    pincode: string;
    city: string;
    state: string;
    country: string;
    phone: string;
  };
  consignee: {
    name: string;
    address: string;
    pincode: string;
    city: string;
    state: string;
    country: string;
    phone: string;
  };
}

/**
 * Generate barcode as SVG string
 */
const generateBarcodeSVG = (text: string): string => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  try {
    JsBarcode(svg, text, {
      format: 'CODE128',
      width: 2,
      height: 50,
      displayValue: false,
      margin: 0,
    });
    return svg.outerHTML;
  } catch (error) {
    console.error('Error generating barcode:', error);
    return '';
  }
};

/**
 * Generate Address Label HTML for a single parcel
 */
const generateAddressLabelHTML = (data: AddressLabelData): string => {
  const barcodeSVG = generateBarcodeSVG(data.parcelNo);

  return `
    <div class="label-page">
      <!-- Shipper Section -->
      <div class="label-section shipper-section">
        <div class="section-header">
          <span class="section-title">Shipper</span>
          <span class="parcel-count">${data.parcelNo.split('/')[1] || '1'}/ ${data.totalParcels}</span>
        </div>
        <div class="section-content">
          <div class="info-grid">
            <div class="info-left">
              <div class="info-row">
                <span class="info-label">Name:</span>
                <span class="info-value name-value">${data.shipper.name}</span>
              </div>
              <div class="info-row address-row">
                <span class="info-label">Address:</span>
                <span class="info-value">${data.shipper.address}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Pincode:</span>
                <span class="info-value">${data.shipper.pincode}</span>
              </div>
              <div class="info-row">
                <span class="info-label">City:</span>
                <span class="info-value">${data.shipper.city}</span>
              </div>
              <div class="info-row">
                <span class="info-label">State:</span>
                <span class="info-value">${data.shipper.state}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Country:</span>
                <span class="info-value">${data.shipper.country}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Phone No.</span>
                <span class="info-value">${data.shipper.phone}</span>
              </div>
            </div>
            <div class="info-right">
              <div class="awb-info">
                <div class="awb-label">AWB No:- ${data.awbNumber}</div>
                <div class="parcel-label">Parcel No:-${data.parcelNo}</div>
              </div>
              <div class="barcode-container">
                ${barcodeSVG}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Consignee Section -->
      <div class="label-section consignee-section">
        <div class="section-header">
          <span class="section-title">Consignee</span>
          <span class="parcel-count">${data.parcelNo.split('/')[1] || '1'}/ ${data.totalParcels}</span>
        </div>
        <div class="section-content">
          <div class="info-grid">
            <div class="info-left">
              <div class="info-row">
                <span class="info-label">Name:</span>
                <span class="info-value name-value">${data.consignee.name}</span>
              </div>
              <div class="info-row address-row">
                <span class="info-label">Address:</span>
                <span class="info-value">${data.consignee.address}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Pincode:</span>
                <span class="info-value">${data.consignee.pincode}</span>
              </div>
              <div class="info-row">
                <span class="info-label">City:</span>
                <span class="info-value">${data.consignee.city}</span>
              </div>
              <div class="info-row">
                <span class="info-label">State:</span>
                <span class="info-value">${data.consignee.state}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Country:</span>
                <span class="info-value">${data.consignee.country}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Phone No.</span>
                <span class="info-value">${data.consignee.phone}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Phone No.</span>
                <span class="info-value"></span>
              </div>
            </div>
            <div class="info-right">
              <div class="awb-info">
                <div class="awb-label">AWB No:- ${data.awbNumber}</div>
                <div class="parcel-label">Parcel No:-${data.parcelNo}</div>
              </div>
              <div class="barcode-container">
                ${barcodeSVG}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
};

/**
 * Get full address from shipper/consignee data
 */
const getFullAddress = (data: {
  address1?: string;
  address2?: string;
  address3?: string;
}): string => {
  const parts = [data.address1, data.address2, data.address3].filter(Boolean);
  return parts.join(', ');
};

/**
 * Get country name from country object or string
 * Handles multiple formats:
 * - Object with name property: { name: "INDIA", ... }
 * - Object with countryName property: { countryName: "INDIA", ... }
 * - Direct string country name: "INDIA"
 * - ObjectId string (24 hex chars): looks up in countriesMap or returns empty
 */
const getCountryName = (country: any, countriesMap?: CountryLookup): string => {
  if (!country) return '';
  
  // If it's an object, try to get the name
  if (typeof country === 'object' && country !== null) {
    // Direct name properties
    if (country.name) return country.name;
    if (country.countryName) return country.countryName;
    
    // Try _id lookup if we have a map
    if (countriesMap && country._id) {
      const lookupName = countriesMap[country._id];
      if (lookupName) return lookupName;
    }
    
    // Try code as fallback
    if (country.code) return country.code;
    
    return '';
  }
  
  // If it's a string
  if (typeof country === 'string') {
    // Check if it looks like a MongoDB ObjectId (24 hex characters)
    const isObjectId = /^[a-f0-9]{24}$/i.test(country);
    if (isObjectId) {
      // Try to look up in the countries map
      if (countriesMap && countriesMap[country]) {
        return countriesMap[country];
      }
      // This is an unpopulated ObjectId with no lookup available
      return '';
    }
    // It's a regular country name string
    return country;
  }
  
  return '';
};

/**
 * Generate Address Labels for all parcels in an AWB
 */
export const generateAddressLabels = (awbData: AWBDetail, countriesMap?: CountryLookup): string => {
  const totalParcels = awbData.totalPieces || awbData.weightDimensions?.length || 1;
  const labels: string[] = [];

  // Generate a label for each parcel
  for (let i = 1; i <= totalParcels; i++) {
    const parcelNo = `${awbData.awbNumber}${String(i).padStart(2, '0')}`;

    const labelData: AddressLabelData = {
      awbNumber: `${awbData.awbNumber}/01`,
      parcelNo: parcelNo,
      totalParcels: totalParcels,
      shipper: {
        name: (awbData.shipper?.personName || awbData.shipper?.company || '').toUpperCase(),
        address: getFullAddress(awbData.shipper || {}).toUpperCase(),
        pincode: (awbData.shipper?.postZipCode || '').toUpperCase(),
        city: (awbData.shipper?.city || '').toUpperCase(),
        state: (awbData.shipper?.stateCounty || '').toUpperCase(),
        country: (getCountryName(awbData.shipper?.country, countriesMap) || '').toUpperCase(),
        phone: awbData.shipper?.phoneNumber || '',
      },
      consignee: {
        name: (awbData.consignee?.personName || awbData.consignee?.company || '').toUpperCase(),
        address: getFullAddress(awbData.consignee || {}).toUpperCase(),
        pincode: (awbData.consignee?.postZipCode || '').toUpperCase(),
        city: (awbData.consignee?.city || '').toUpperCase(),
        state: (awbData.consignee?.stateCounty || '').toUpperCase(),
        country: (getCountryName(awbData.consignee?.country, countriesMap) || '').toUpperCase(),
        phone: awbData.consignee?.phoneNumber || '',
      },
    };

    labels.push(generateAddressLabelHTML(labelData));
  }

  return labels.join('\n');
};

/**
 * Full HTML document for printing
 */
export const getAddressLabelDocument = (awbData: AWBDetail, countriesMap?: CountryLookup): string => {
  const labelsHTML = generateAddressLabels(awbData, countriesMap);

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Address Label - AWB ${awbData.awbNumber}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    @page {
      size: A4;
      margin: 10mm;
    }

    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 12px;
      line-height: 1.3;
      color: #000;
      background: #fff;
    }

    .label-page {
      width: 100%;
      max-width: 210mm;
      margin: 0 auto;
      padding: 5mm;
      page-break-after: always;
    }

    .label-page:last-child {
      page-break-after: auto;
    }

    .label-section {
      border: 2px solid #000;
      margin-bottom: 10mm;
    }

    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 5px 10px;
      border-bottom: 1px solid #000;
      background: #fff;
    }

    .section-title {
      font-size: 24px;
      font-weight: bold;
    }

    .parcel-count {
      font-size: 24px;
      font-weight: bold;
    }

    .section-content {
      padding: 10px;
    }

    .info-grid {
      display: flex;
      justify-content: space-between;
    }

    .info-left {
      flex: 1;
      padding-right: 20px;
    }

    .info-right {
      width: 200px;
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      justify-content: space-between;
    }

    .info-row {
      display: flex;
      margin-bottom: 3px;
    }

    .info-label {
      font-weight: bold;
      min-width: 80px;
      flex-shrink: 0;
    }

    .info-value {
      flex: 1;
    }

    .name-value {
      font-weight: bold;
      text-decoration: underline;
      font-size: 14px;
    }

    .address-row .info-value {
      white-space: pre-line;
    }

    .awb-info {
      text-align: right;
      margin-bottom: 10px;
    }

    .awb-label {
      font-weight: bold;
      font-size: 12px;
      margin-bottom: 2px;
    }

    .parcel-label {
      font-weight: bold;
      font-size: 12px;
    }

    .barcode-container {
      margin-top: 5px;
    }

    .barcode-container svg {
      width: 180px;
      height: 50px;
    }

    @media print {
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .label-page {
        page-break-inside: avoid;
      }

      .no-print {
        display: none !important;
      }
    }

    /* Print button styling */
    .print-controls {
      position: fixed;
      top: 10px;
      right: 10px;
      z-index: 1000;
      background: #fff;
      padding: 10px;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    }

    .print-btn {
      background: #2563eb;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      margin-right: 10px;
    }

    .print-btn:hover {
      background: #1d4ed8;
    }

    .close-btn {
      background: #6b7280;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
    }

    .close-btn:hover {
      background: #4b5563;
    }
  </style>
</head>
<body>
  <div class="print-controls no-print">
    <button class="print-btn" onclick="window.print()">🖨️ Print / Save as PDF</button>
    <button class="close-btn" onclick="window.close()">✕ Close</button>
  </div>
  ${labelsHTML}
</body>
</html>
  `;
};

/**
 * Open Address Label in new window for printing/downloading
 */
export const openAddressLabelPrint = (awbData: AWBDetail, countriesMap?: CountryLookup): void => {
  const htmlContent = getAddressLabelDocument(awbData, countriesMap);
  const printWindow = window.open('', '_blank');

  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  }
};

/**
 * Download Address Label as HTML file (can be opened and printed)
 */
export const downloadAddressLabelHTML = (awbData: AWBDetail, countriesMap?: CountryLookup): void => {
  const htmlContent = getAddressLabelDocument(awbData, countriesMap);
  const blob = new Blob([htmlContent], { type: 'text/html' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = `Address_Label_${awbData.awbNumber}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// ============================================================================
// BOX LABEL GENERATION
// ============================================================================

interface BoxLabelData {
  bookingDate: string;
  productType: string;
  boxNumber: number;
  totalBoxes: number;
  consignee: {
    name: string;
    company: string;
    address: string;
    cityStatePin: string;
    country: string;
    phone: string;
  };
  shipper: {
    name: string;
    company: string;
    address: string;
    cityStatePin: string;
    country: string;
  };
  awbNumber: string;
  parcelNo: string;
  actualWeight: number;
  serviceName: string;
}

/**
 * Generate a large vertical barcode SVG for box labels
 */
const generateVerticalBarcodeSVG = (text: string): string => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  try {
    JsBarcode(svg, text, {
      format: 'CODE128',
      width: 2,
      height: 60,
      displayValue: false,
      margin: 0,
    });
    return svg.outerHTML;
  } catch (error) {
    console.error('Error generating vertical barcode:', error);
    return '';
  }
};

/**
 * Generate a large horizontal barcode SVG for parcel number
 */
const generateLargeBarcodeSVG = (text: string): string => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  try {
    JsBarcode(svg, text, {
      format: 'CODE128',
      width: 2.5,
      height: 70,
      displayValue: false,
      margin: 0,
    });
    return svg.outerHTML;
  } catch (error) {
    console.error('Error generating large barcode:', error);
    return '';
  }
};

/**
 * Format date for box label
 */
const formatBoxLabelDate = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

/**
 * Generate Box Label HTML for a single box (full A4 page)
 */
const generateBoxLabelHTML = (data: BoxLabelData): string => {
  const awbBarcodeSVG = generateVerticalBarcodeSVG(data.awbNumber);
  const parcelBarcodeSVG = generateLargeBarcodeSVG(data.parcelNo);

  return `
    <div class="box-label-page">
      <!-- Header Row: Date, Product Type, Box Count -->
      <div class="box-header">
        <div class="header-cell date-cell">${data.bookingDate}</div>
        <div class="header-cell product-cell">${data.productType}</div>
        <div class="header-cell count-cell">${data.boxNumber}/${data.totalBoxes}</div>
      </div>

      <!-- Main Content Area -->
      <div class="box-main-content">
        <!-- Left Side: Address Information -->
        <div class="address-section">
          <!-- Consignee Section -->
          <div class="consignee-block">
            <div class="block-title">CONSIGNEE</div>
            <div class="address-content">
              <div class="address-line name-line">${data.consignee.name}</div>
              <div class="address-line company-line">${data.consignee.company}</div>
              <div class="address-line">${data.consignee.address}</div>
              <div class="address-line">${data.consignee.cityStatePin}</div>
              <div class="address-line country-line">${data.consignee.country}</div>
              <div class="address-line phone-line">TEL : ${data.consignee.phone}</div>
            </div>
          </div>

          <!-- Shipper Section -->
          <div class="shipper-block">
            <div class="block-title">SHIPPER</div>
            <div class="address-content">
              <div class="address-line name-line">${data.shipper.name}</div>
              <div class="address-line company-line">${data.shipper.company}</div>
              <div class="address-line">${data.shipper.address}</div>
              <div class="address-line">${data.shipper.cityStatePin}</div>
              <div class="address-line country-line">${data.shipper.country}</div>
            </div>
          </div>

          <!-- Weight Section -->
          <div class="weight-block">
            <div class="weight-text">Actual Box weight : ${data.actualWeight.toFixed(2)} KG</div>
          </div>
        </div>

        <!-- Right Side: Vertical Barcode & AWB -->
        <div class="barcode-section">
          <div class="barcode-container">
            <div class="awb-barcode-wrapper">
              ${awbBarcodeSVG}
            </div>
            <div class="awb-text-vertical">
              <span class="awb-label">AWB NO.</span>
              <span class="awb-number">${data.awbNumber}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom Section: Service and Parcel Barcode -->
      <div class="box-footer">
        <div class="service-name">${data.serviceName}</div>
        <div class="parcel-section">
          <div class="parcel-title">PARCEL NO.</div>
          <div class="parcel-barcode-wrapper">
            ${parcelBarcodeSVG}
          </div>
          <div class="parcel-number">${data.parcelNo}</div>
        </div>
      </div>
    </div>
  `;
};

/**
 * Get product type name from product object
 */
const getProductTypeName = (product: any): string => {
  if (!product) return 'NONDOX';
  if (typeof product === 'string') return product;
  if (typeof product === 'object') {
    return product.name || product.code || 'NONDOX';
  }
  return 'NONDOX';
};

/**
 * Get service name from service object
 */
const getServiceName = (service: any): string => {
  if (!service) return '';
  if (typeof service === 'string') return service;
  if (typeof service === 'object') {
    return service.name || service.code || '';
  }
  return '';
};

/**
 * Generate Box Labels for all boxes in an AWB
 */
export const generateBoxLabels = (awbData: AWBDetail, countriesMap?: CountryLookup): string => {
  const weightDimensions = awbData.weightDimensions || [];
  const totalBoxes = weightDimensions.length || awbData.totalPieces || 1;
  const labels: string[] = [];

  // If no weight dimensions, create at least one box
  if (weightDimensions.length === 0) {
    const boxData: BoxLabelData = {
      bookingDate: formatBoxLabelDate(awbData.bookingDate).toUpperCase(),
      productType: getProductTypeName(awbData.product).toUpperCase(),
      boxNumber: 1,
      totalBoxes: 1,
      consignee: {
        name: (awbData.consignee?.personName || '').toUpperCase(),
        company: (awbData.consignee?.company || awbData.consignee?.personName || '').toUpperCase(),
        address: getFullAddress(awbData.consignee || {}).toUpperCase(),
        cityStatePin: `${awbData.consignee?.city || ''}, ${awbData.consignee?.stateCounty || ''}, ${awbData.consignee?.postZipCode || ''}`.toUpperCase(),
        country: getCountryName(awbData.consignee?.country, countriesMap).toUpperCase(),
        phone: awbData.consignee?.phoneNumber || '',
      },
      shipper: {
        name: (awbData.shipper?.personName || '').toUpperCase(),
        company: (awbData.shipper?.company || awbData.shipper?.personName || '').toUpperCase(),
        address: getFullAddress(awbData.shipper || {}).toUpperCase(),
        cityStatePin: `${awbData.shipper?.city || ''}, ${awbData.shipper?.stateCounty || ''}, ${awbData.shipper?.postZipCode || ''}`.toUpperCase(),
        country: getCountryName(awbData.shipper?.country, countriesMap).toUpperCase(),
      },
      awbNumber: awbData.awbNumber,
      parcelNo: `${awbData.awbNumber} / 01`,
      actualWeight: awbData.totalActualWeight || 0,
      serviceName: getServiceName(awbData.service).toUpperCase(),
    };
    labels.push(generateBoxLabelHTML(boxData));
  } else {
    // Generate a label for each box
    for (let i = 0; i < weightDimensions.length; i++) {
      const box = weightDimensions[i];
      const boxNumber = box.boxNo || i + 1;
      const parcelNo = `${awbData.awbNumber} / ${String(boxNumber).padStart(2, '0')}`;

      const boxData: BoxLabelData = {
        bookingDate: formatBoxLabelDate(awbData.bookingDate).toUpperCase(),
        productType: getProductTypeName(awbData.product).toUpperCase(),
        boxNumber: boxNumber,
        totalBoxes: totalBoxes,
        consignee: {
          name: (awbData.consignee?.personName || '').toUpperCase(),
          company: (awbData.consignee?.company || awbData.consignee?.personName || '').toUpperCase(),
          address: getFullAddress(awbData.consignee || {}).toUpperCase(),
          cityStatePin: `${awbData.consignee?.city || ''}, ${awbData.consignee?.stateCounty || ''}, ${awbData.consignee?.postZipCode || ''}`.toUpperCase(),
          country: getCountryName(awbData.consignee?.country, countriesMap).toUpperCase(),
          phone: awbData.consignee?.phoneNumber || '',
        },
        shipper: {
          name: (awbData.shipper?.personName || '').toUpperCase(),
          company: (awbData.shipper?.company || awbData.shipper?.personName || '').toUpperCase(),
          address: getFullAddress(awbData.shipper || {}).toUpperCase(),
          cityStatePin: `${awbData.shipper?.city || ''}, ${awbData.shipper?.stateCounty || ''}, ${awbData.shipper?.postZipCode || ''}`.toUpperCase(),
          country: getCountryName(awbData.shipper?.country, countriesMap).toUpperCase(),
        },
        awbNumber: awbData.awbNumber,
        parcelNo: parcelNo,
        actualWeight: box.actualWeight || 0,
        serviceName: getServiceName(awbData.service).toUpperCase(),
      };

      labels.push(generateBoxLabelHTML(boxData));
    }
  }

  return labels.join('\n');
};

/**
 * Full HTML document for Box Label printing
 */
export const getBoxLabelDocument = (awbData: AWBDetail, countriesMap?: CountryLookup): string => {
  const labelsHTML = generateBoxLabels(awbData, countriesMap);

  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Box Label - AWB ${awbData.awbNumber}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    @page {
      size: A4 portrait;
      margin: 5mm;
    }

    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 14px;
      line-height: 1.2;
      color: #000;
      background: #fff;
    }

    .box-label-page {
      width: 200mm;
      height: 287mm;
      margin: 0 auto;
      padding: 3mm;
      border: 3px solid #1a1a2e;
      page-break-after: always;
      display: flex;
      flex-direction: column;
    }

    .box-label-page:last-child {
      page-break-after: auto;
    }

    /* Header Row */
    .box-header {
      display: flex;
      border-bottom: 2px solid #000;
    }

    .header-cell {
      padding: 8px 15px;
      font-size: 28px;
      font-weight: bold;
      border-right: 2px solid #000;
    }

    .header-cell:last-child {
      border-right: none;
    }

    .date-cell {
      flex: 1;
    }

    .product-cell {
      flex: 1.5;
      text-align: center;
    }

    .count-cell {
      width: 100px;
      text-align: center;
    }

    /* Main Content */
    .box-main-content {
      display: flex;
      flex: 1;
      border-bottom: 2px solid #000;
    }

    .address-section {
      flex: 1;
      display: flex;
      flex-direction: column;
      border-right: 2px solid #000;
    }

    .consignee-block,
    .shipper-block {
      padding: 10px 15px;
      border-bottom: 2px solid #000;
    }

    .consignee-block {
      flex: 1.2;
    }

    .shipper-block {
      flex: 1;
    }

    .block-title {
      font-size: 22px;
      font-weight: bold;
      margin-bottom: 8px;
    }

    .address-content {
      padding-left: 0;
    }

    .address-line {
      font-size: 18px;
      line-height: 1.4;
      font-weight: bold;
    }

    .name-line {
      font-size: 20px;
      margin-bottom: 2px;
    }

    .company-line {
      font-size: 20px;
      margin-bottom: 4px;
    }

    .country-line {
      font-size: 20px;
      margin-top: 4px;
    }

    .phone-line {
      margin-top: 8px;
      font-size: 18px;
    }

    .weight-block {
      padding: 12px 15px;
      border-bottom: none;
    }

    .weight-text {
      font-size: 20px;
      font-weight: bold;
    }

    /* Barcode Section - Professional Layout */
    .barcode-section {
      width: 120px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 15px 10px;
      background: #fafafa;
    }

    .barcode-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 100%;
      width: 100%;
    }

    .awb-barcode-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      transform: rotate(90deg);
      margin: 60px 0;
    }

    .awb-barcode-wrapper svg {
      width: 200px;
      height: 60px;
    }

    .awb-text-vertical {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      writing-mode: vertical-rl;
      text-orientation: mixed;
      transform: rotate(180deg);
      margin-top: 20px;
    }

    .awb-label {
      font-size: 12px;
      font-weight: bold;
      margin-bottom: 8px;
      letter-spacing: 1px;
    }

    .awb-number {
      font-size: 24px;
      font-weight: bold;
      letter-spacing: 2px;
    }

    /* Footer */
    .box-footer {
      padding: 15px 20px;
      text-align: center;
      border-top: 2px solid #000;
    }

    .service-name {
      font-size: 32px;
      font-weight: bold;
      margin-bottom: 15px;
      text-transform: uppercase;
      letter-spacing: 2px;
    }

    .parcel-section {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 10px 0;
    }

    .parcel-title {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 10px;
      letter-spacing: 1px;
    }

    .parcel-barcode-wrapper {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 10px 0;
    }

    .parcel-barcode-wrapper svg {
      width: 400px;
      height: 70px;
    }

    .parcel-number {
      font-size: 26px;
      font-weight: bold;
      margin-top: 8px;
      letter-spacing: 3px;
    }

    @media print {
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .box-label-page {
        page-break-inside: avoid;
      }

      .no-print {
        display: none !important;
      }
    }

    /* Print button styling */
    .print-controls {
      position: fixed;
      top: 10px;
      right: 10px;
      z-index: 1000;
      background: #fff;
      padding: 10px;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    }

    .print-btn {
      background: #2563eb;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      margin-right: 10px;
    }

    .print-btn:hover {
      background: #1d4ed8;
    }

    .close-btn {
      background: #6b7280;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
    }

    .close-btn:hover {
      background: #4b5563;
    }
  </style>
</head>
<body>
  <div class="print-controls no-print">
    <button class="print-btn" onclick="window.print()">🖨️ Print / Save as PDF</button>
    <button class="close-btn" onclick="window.close()">✕ Close</button>
  </div>
  ${labelsHTML}
</body>
</html>
  `;
};

/**
 * Open Box Label in new window for printing/downloading
 */
export const openBoxLabelPrint = (awbData: AWBDetail, countriesMap?: CountryLookup): void => {
  const htmlContent = getBoxLabelDocument(awbData, countriesMap);
  const printWindow = window.open('', '_blank');

  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  }
};

/**
 * Convert number to words for invoice amount
 */
const numberToWords = (num: number): string => {
  const ones = ['', 'ONE', 'TWO', 'THREE', 'FOUR', 'FIVE', 'SIX', 'SEVEN', 'EIGHT', 'NINE',
    'TEN', 'ELEVEN', 'TWELVE', 'THIRTEEN', 'FOURTEEN', 'FIFTEEN', 'SIXTEEN', 'SEVENTEEN', 'EIGHTEEN', 'NINETEEN'];
  const tens = ['', '', 'TWENTY', 'THIRTY', 'FORTY', 'FIFTY', 'SIXTY', 'SEVENTY', 'EIGHTY', 'NINETY'];

  const convertLessThanThousand = (n: number): string => {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' HUNDRED' + (n % 100 !== 0 ? ' AND ' + convertLessThanThousand(n % 100) : '');
  };

  if (num === 0) return 'ZERO';

  const crore = Math.floor(num / 10000000);
  const lakh = Math.floor((num % 10000000) / 100000);
  const thousand = Math.floor((num % 100000) / 1000);
  const remainder = num % 1000;

  let result = '';
  if (crore > 0) result += convertLessThanThousand(crore) + ' CRORE ';
  if (lakh > 0) result += convertLessThanThousand(lakh) + ' LAKH ';
  if (thousand > 0) result += convertLessThanThousand(thousand) + ' THOUSAND ';
  if (remainder > 0) result += convertLessThanThousand(remainder);

  return result.trim();
};

/**
 * Get currency code from currency object or string
 */
const getCurrencyCode = (currency: any): string => {
  if (!currency) return 'INR';
  if (typeof currency === 'string') return currency;
  if (typeof currency === 'object') {
    return currency.code || currency.currencyCode || currency.name || 'INR';
  }
  return 'INR';
};

/**
 * Get currency name for display
 */
const getCurrencyName = (currency: any): string => {
  const currencyCode = getCurrencyCode(currency);
  const currencyNames: { [key: string]: string } = {
    'INR': 'RUPEES',
    'USD': 'DOLLARS',
    'EUR': 'EUROS',
    'GBP': 'POUNDS',
    'AED': 'DIRHAMS',
    'SGD': 'SINGAPORE DOLLARS',
    'AUD': 'AUSTRALIAN DOLLARS',
    'CAD': 'CANADIAN DOLLARS',
  };
  return currencyNames[currencyCode.toUpperCase()] || currencyCode || 'RUPEES';
};

/**
 * Get currency symbol
 */
const getCurrencySymbol = (currency: any): string => {
  const currencyCode = getCurrencyCode(currency);
  const currencySymbols: { [key: string]: string } = {
    'INR': '₹',
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'AED': 'AED',
    'SGD': 'S$',
    'AUD': 'A$',
    'CAD': 'C$',
  };
  return currencySymbols[currencyCode.toUpperCase()] || currencyCode || '₹';
};

/**
 * Format date as DD/MM/YYYY
 */
const formatInvoiceDate = (dateStr?: string): string => {
  if (!dateStr) return new Date().toLocaleDateString('en-GB');
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB');
  } catch {
    return new Date().toLocaleDateString('en-GB');
  }
};

/**
 * Generate Proforma Invoice HTML
 */
const generateProformaInvoiceHTML = (awbData: AWBDetail, countriesMap?: CountryLookup): string => {
  const invoice = awbData.shipmentInvoice;
  if (!invoice || !invoice.lineItems || invoice.lineItems.length === 0) {
    return '<div>No invoice data available</div>';
  }

  // Get shipper details
  const shipperName = awbData.shipper?.personName || '';
  const shipperCompany = awbData.shipper?.company || '';
  const shipperAddress = [
    awbData.shipper?.address1,
    awbData.shipper?.address2,
    awbData.shipper?.address3
  ].filter(Boolean).join(', ');
  const shipperCity = awbData.shipper?.city || '';
  const shipperState = awbData.shipper?.stateCounty || '';
  // Try shipper.country first, then fall back to origin (which might be populated)
  const shipperCountry = getCountryName(awbData.shipper?.country, countriesMap) || getCountryName(awbData.origin, countriesMap);
  const shipperPincode = awbData.shipper?.postZipCode || '';
  const shipperEmail = awbData.shipper?.emailAddress || '';
  const shipperPhone = awbData.shipper?.phoneNumber || '';
  const shipperKycType = awbData.shipper?.kycType || '';
  const shipperKycNumber = awbData.shipper?.kycNumber || '';

  // Get consignee details
  const consigneeName = awbData.consignee?.personName || '';
  const consigneeCompany = awbData.consignee?.company || '';
  const consigneeAddress = [
    awbData.consignee?.address1,
    awbData.consignee?.address2,
    awbData.consignee?.address3
  ].filter(Boolean).join(', ');
  const consigneeCity = awbData.consignee?.city || '';
  const consigneeState = awbData.consignee?.stateCounty || '';
  // Try consignee.country first, then fall back to destination (which might be populated)
  const consigneeCountry = getCountryName(awbData.consignee?.country, countriesMap) || getCountryName(awbData.destination, countriesMap);
  const consigneePincode = awbData.consignee?.postZipCode || '';
  const consigneeEmail = awbData.consignee?.emailAddress || '';
  const consigneePhone = awbData.consignee?.phoneNumber || '';

  // Get invoice details - handle currency as object or string
  const currencyRaw = invoice.currency || 'INR';
  const currencyCode = getCurrencyCode(currencyRaw);
  const currencySymbol = getCurrencySymbol(currencyRaw);
  const currencyName = getCurrencyName(currencyRaw);

  // Calculate totals
  let totalAmount = 0;
  const lineItemsHTML = invoice.lineItems.map((item, index) => {
    const amount = (item.quantity || 1) * (item.unitRate || 0);
    totalAmount += amount;
    
    return `
      <tr>
        <td>${index + 1}</td>
        <td>${item.description || '-'}</td>
        <td>${item.hsCode || '-'}</td>
        <td>${item.igst || '-'}</td>
        <td>${item.unitType || 'PCS'}</td>
        <td>${item.quantity || 1}</td>
        <td>${item.unitRate?.toFixed(2) || '0.00'}</td>
        <td>${amount.toFixed(2)}</td>
      </tr>
    `;
  }).join('');

  // Group items by box
  const boxGroups: { [key: string]: typeof invoice.lineItems } = {};
  invoice.lineItems.forEach(item => {
    const boxKey = item.boxNo || '1';
    if (!boxGroups[boxKey]) {
      boxGroups[boxKey] = [];
    }
    boxGroups[boxKey].push(item);
  });

  // Generate grouped line items HTML with box headers
  let groupedLineItemsHTML = '';
  let srNo = 1;
  
  Object.keys(boxGroups).sort().forEach(boxNo => {
    const items = boxGroups[boxNo];
    // Find matching weight dimension for this box
    const weightDim = awbData.weightDimensions?.find(w => w.boxNo?.toString() === boxNo);
    const dimensions = weightDim 
      ? `${weightDim.length?.toFixed(2) || '0'}*${weightDim.breadth?.toFixed(2) || '0'}*${weightDim.height?.toFixed(2) || '0'}`
      : '-';
    const boxWeight = weightDim?.actualWeight?.toFixed(2) || '-';

    groupedLineItemsHTML += `
      <tr class="box-header-row">
        <td colspan="8" class="box-header">
          <strong>BOX NO.: ${boxNo}</strong>&nbsp;&nbsp;
          DIMENSIONS (CMS) ${dimensions}&nbsp;&nbsp;
          ACTUAL WEIGHT - ${boxWeight}KG
        </td>
      </tr>
    `;

    items.forEach(item => {
      const amount = (item.quantity || 1) * (item.unitRate || 0);
      groupedLineItemsHTML += `
        <tr>
          <td>${srNo++}</td>
          <td>${item.description || '-'}</td>
          <td>${item.hsCode || '-'}</td>
          <td>${item.igst || '-'}</td>
          <td>${item.unitType || 'PCS'}</td>
          <td>${item.quantity || 1}</td>
          <td>${item.unitRate?.toFixed(2) || '0.00'}</td>
          <td>${amount.toFixed(2)}</td>
        </tr>
      `;
    });
  });

  // No extra empty rows - keep it clean

  const totalAmountInWords = `${numberToWords(Math.floor(totalAmount))} ${currencyName} ONLY`;
  const invoiceDate = formatInvoiceDate(awbData.invoiceDate || awbData.bookingDate);

  return `
    <div class="invoice-page">
      <div class="invoice-content">
        <h1 class="invoice-title">PROFORMA INVOICE</h1>
        
        <div class="invoice-main-table">
          <table class="outer-table">
            <!-- Header Row -->
            <tr>
              <td class="left-col header-section">
                <div class="header-line"><strong>INVOICE NO. : </strong>${awbData.invoiceNumber || awbData.awbNumber}</div>
                <div class="header-line"><strong>INVOICE DATE. : </strong>${invoiceDate}</div>
                <div class="header-line"><strong>TOTAL PIECES : </strong>${awbData.totalPieces || 1}</div>
                <div class="header-line"><strong>ACTUAL WEIGHT : </strong>${awbData.totalActualWeight?.toFixed(2) || '0.00'}</div>
                <div class="header-line"><strong>VOLUMETRIC WEIGHT : </strong>${awbData.totalVolumetricWeight?.toFixed(2) || '0.00'}</div>
              </td>
              <td class="right-col header-section">
                <div class="header-line"><strong>OTHER REFERENCE</strong></div>
                ${shipperKycType && shipperKycNumber ? `<div class="header-line"><strong>${shipperKycType.toUpperCase()} NUMBER: </strong>${shipperKycNumber}</div>` : ''}
                ${awbData.referenceNumber ? `<div class="header-line"><strong>REF: </strong>${awbData.referenceNumber}</div>` : ''}
              </td>
            </tr>

            <!-- Shipper / Consignee Row -->
            <tr>
              <td class="left-col address-section">
                <div class="section-label">SHIPPER</div>
                <div class="address-content">
                  <div>${shipperName}</div>
                  ${shipperCompany ? `<div>COMPANY NAME : ${shipperCompany}</div>` : ''}
                  <div>${shipperAddress}</div>
                  <div>${[shipperCity, shipperState, shipperCountry, shipperPincode].filter(Boolean).join(', ')}</div>
                  ${shipperEmail ? `<div>EMAIL : ${shipperEmail}</div>` : ''}
                  <div>PHONE NUMBER : ${shipperPhone}</div>
                </div>
              </td>
              <td class="right-col address-section">
                <div class="section-label">CONSIGNEE</div>
                <div class="address-content">
                  <div>${consigneeName}</div>
                  ${consigneeCompany ? `<div>COMPANY NAME : ${consigneeCompany}</div>` : ''}
                  <div>${consigneeAddress}</div>
                  <div>${[consigneeCity, consigneeState, consigneeCountry, consigneePincode].filter(Boolean).join(', ')}</div>
                  ${consigneeEmail ? `<div>EMAIL : ${consigneeEmail}</div>` : ''}
                  <div>PHONE NUMBER : ${consigneePhone}</div>
                </div>
              </td>
            </tr>
          </table>

          <!-- Line Items Table -->
          <div class="items-wrapper">
            <table class="items-table">
              <thead>
                <tr>
                  <th class="col-sr">SR. NO.</th>
                  <th class="col-desc">DESCRIPTION</th>
                  <th class="col-hs">HS CODE</th>
                  <th class="col-cto">C/T/O</th>
                  <th class="col-unit">UNIT TYPE</th>
                  <th class="col-qty">QUANTITY</th>
                  <th class="col-rate">UNIT RATE</th>
                  <th class="col-amt">AMOUNT</th>
                </tr>
              </thead>
              <tbody>
                ${groupedLineItemsHTML}
              </tbody>
            </table>
          </div>
          <!-- Spacer to push footer down -->
          <div class="items-spacer"></div>
        </div>
      </div>

      <!-- Footer Section - Always at bottom -->
      <div class="invoice-footer">
        <!-- Amount in Words -->
        <div class="amount-words-row">
          <div class="amount-words-label">AMOUNT CHARGEABLE : ${totalAmountInWords}</div>
          <div class="amount-total">TOTAL: ${totalAmount.toFixed(2)} ${currencyCode}</div>
        </div>

        <!-- Notes and Signature -->
        <table class="footer-table">
          <tr>
            <td class="notes-section">
              <div class="section-label">NOTES</div>
              <div class="notes-content">${invoice.note || invoice.remarks || ''}</div>
            </td>
            <td class="signature-section">
              <div class="section-label">SIGNATURE / STAMP</div>
              <div class="signature-area"></div>
            </td>
          </tr>
        </table>
      </div>
    </div>
  `;
};

/**
 * Get complete Proforma Invoice document HTML
 */
const getProformaInvoiceDocument = (awbData: AWBDetail, countriesMap?: CountryLookup): string => {
  const invoiceHTML = generateProformaInvoiceHTML(awbData, countriesMap);

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Proforma Invoice - ${awbData.awbNumber}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    html, body {
      height: 100%;
    }

    body {
      font-family: Arial, sans-serif;
      font-size: 12px;
      line-height: 1.4;
      color: #000;
      background: #f5f5f5;
      padding: 15px;
    }

    .invoice-page {
      width: 210mm;
      height: 297mm;
      margin: 0 auto;
      padding: 12mm;
      background: white;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      display: flex;
      flex-direction: column;
    }

    .invoice-content {
      flex: 1;
      display: flex;
      flex-direction: column;
    }

    .invoice-title {
      text-align: center;
      font-size: 26px;
      font-weight: bold;
      margin-bottom: 12px;
      letter-spacing: 3px;
    }

    .invoice-main-table {
      flex: 1;
      display: flex;
      flex-direction: column;
    }

    .outer-table {
      width: 100%;
      border-collapse: collapse;
      border: 1.5px solid #000;
    }

    .outer-table td {
      border: 1.5px solid #000;
      vertical-align: top;
      padding: 10px;
    }

    .left-col {
      width: 50%;
    }

    .right-col {
      width: 50%;
    }

    .header-section {
      min-height: 90px;
    }

    .header-line {
      margin-bottom: 4px;
      font-size: 12px;
    }

    .section-label {
      font-weight: bold;
      background: #e0e0e0;
      padding: 6px 10px;
      margin: -10px -10px 10px -10px;
      border-bottom: 1.5px solid #000;
      font-size: 13px;
    }

    .address-section {
      min-height: 140px;
    }

    .address-content {
      line-height: 1.6;
      font-size: 12px;
    }

    .address-content div {
      margin-bottom: 3px;
    }

    /* Items Table Wrapper - grows to fill available space */
    .items-wrapper {
      flex: 1;
      display: flex;
      flex-direction: column;
      border: 1.5px solid #000;
      border-top: none;
      min-height: 0;
    }

    /* Items Table */
    .items-table {
      width: 100%;
      border-collapse: collapse;
    }

    .items-table th,
    .items-table td {
      border: 1px solid #000;
      border-left: none;
      border-right: none;
      padding: 6px 5px;
      text-align: left;
    }

    .items-table th:first-child,
    .items-table td:first-child {
      border-left: none;
    }

    .items-table th:last-child,
    .items-table td:last-child {
      border-right: none;
    }

    .items-table thead tr {
      border-bottom: 1.5px solid #000;
    }

    .items-table th {
      background: #f0f0f0;
      font-weight: bold;
      text-align: center;
      font-size: 10px;
      padding: 8px 5px;
    }

    .items-table td {
      text-align: center;
      vertical-align: top;
      font-size: 11px;
    }

    .items-table td:nth-child(2) {
      text-align: left;
    }

    .items-table tbody {
      vertical-align: top;
    }

    .col-sr { width: 6%; }
    .col-desc { width: 28%; }
    .col-hs { width: 12%; }
    .col-cto { width: 8%; }
    .col-unit { width: 10%; }
    .col-qty { width: 10%; }
    .col-rate { width: 12%; }
    .col-amt { width: 14%; }

    .box-header-row td {
      background: #f5f5f5;
      text-align: center !important;
      font-size: 10px;
      padding: 5px;
      font-weight: 500;
    }

    .box-header {
      text-align: center;
    }

    /* Spacer to push footer to bottom */
    .items-spacer {
      flex: 1;
      border-left: 1.5px solid #000;
      border-right: 1.5px solid #000;
      min-height: 20px;
    }

    /* Footer Section - stays at bottom */
    .invoice-footer {
      margin-top: auto;
    }

    /* Amount Row */
    .amount-words-row {
      display: flex;
      border: 1.5px solid #000;
      border-top: 1.5px solid #000;
      background: #f5f5f5;
    }

    .amount-words-label {
      flex: 1;
      padding: 12px;
      font-weight: bold;
      font-size: 12px;
      border-right: 1.5px solid #000;
    }

    .amount-total {
      padding: 12px 18px;
      font-weight: bold;
      font-size: 14px;
      min-width: 200px;
      text-align: right;
    }

    /* Footer Table */
    .footer-table {
      width: 100%;
      border-collapse: collapse;
      border: 1.5px solid #000;
      border-top: none;
    }

    .footer-table td {
      border: 1.5px solid #000;
      vertical-align: top;
      padding: 10px;
    }

    .notes-section {
      width: 55%;
      height: 80px;
    }

    .signature-section {
      width: 45%;
      height: 80px;
    }

    .notes-content {
      margin-top: 8px;
      font-size: 11px;
      line-height: 1.5;
    }

    .signature-area {
      height: 50px;
    }

    /* Print Controls */
    .print-controls {
      position: fixed;
      top: 10px;
      right: 10px;
      z-index: 1000;
      background: #fff;
      padding: 10px;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    }

    .print-btn {
      background: #2563eb;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      margin-right: 10px;
    }

    .print-btn:hover {
      background: #1d4ed8;
    }

    .close-btn {
      background: #6b7280;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
    }

    .close-btn:hover {
      background: #4b5563;
    }

    @media print {
      html, body {
        height: auto;
      }

      body {
        padding: 0;
        background: white;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .invoice-page {
        box-shadow: none;
        padding: 8mm;
        width: 100%;
        height: 100vh;
        page-break-inside: avoid;
      }

      .print-controls {
        display: none !important;
      }

      @page {
        size: A4;
        margin: 0;
      }
    }
  </style>
</head>
<body>
  <div class="print-controls">
    <button class="print-btn" onclick="window.print()">🖨️ Print / Save as PDF</button>
    <button class="close-btn" onclick="window.close()">✕ Close</button>
  </div>
  ${invoiceHTML}
</body>
</html>
  `;
};

/**
 * Open Proforma Invoice in new window for printing/downloading
 */
export const openProformaInvoicePrint = (awbData: AWBDetail, countriesMap?: CountryLookup): void => {
  const htmlContent = getProformaInvoiceDocument(awbData, countriesMap);
  const printWindow = window.open('', '_blank');

  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  }
};

/**
 * Check if AWB has shipment invoice data available
 */
export const hasShipmentInvoice = (awbData: AWBDetail): boolean => {
  return !!(
    awbData.shipmentInvoice &&
    awbData.shipmentInvoice.lineItems &&
    awbData.shipmentInvoice.lineItems.length > 0
  );
};

// ============================================================================
// AUTHORIZATION LETTER GENERATION
// ============================================================================

/**
 * Format date for Authorization Letter (DD/MM/YYYY)
 */
const formatAuthorizationDate = (dateStr?: string): string => {
  if (!dateStr) return new Date().toLocaleDateString('en-GB');
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB');
  } catch {
    return new Date().toLocaleDateString('en-GB');
  }
};

/**
 * Generate Authorization Letter HTML
 */
const generateAuthorizationLetterHTML = (awbData: AWBDetail, countriesMap?: CountryLookup): string => {
  // Get shipper details for the letter
  const shipperName = awbData.shipper?.personName || awbData.shipper?.company || '';
  const shipperCompany = awbData.shipper?.company || awbData.shipper?.personName || '';
  const shipperKycType = awbData.shipper?.kycType || '';
  const shipperKycNumber = awbData.shipper?.kycNumber || '';
  
  // Format the date
  const letterDate = formatAuthorizationDate(awbData.bookingDate);

  return `
    <div class="auth-letter-page">
      <!-- Date -->
      <div class="letter-date">DATE : ${letterDate}</div>
      
      <!-- Addressee -->
      <div class="addressee">
        <p><strong>TO</strong></p>
        <p><strong>SAIRAJ INTERNATIONAL,</strong></p>
        <p><strong>GR. FLOOR, D. C. SILK MILLS BUSINESS PARK, CHUNAWALA ESTATE, KONDIVITA ROAD,</strong></p>
        <p><strong>OPP. P. D. HINDUJA COLLEGE OF NURSING, ANDHERI (EAST) Mumbai 400 059. Maharashtra.</strong></p>
        <p><strong>India</strong></p>
      </div>

      <!-- Title -->
      <h1 class="letter-title">Export Authorization with Know Your Customer Document</h1>
      
      <!-- AWB Number -->
      <div class="awb-number">AWB # ${awbData.awbNumber}</div>

      <!-- Authorization Text -->
      <div class="auth-text">
        <p>We hereby authorize Sairaj International Pvt. Ltd (hereinafter Sairaj, which expression shall include their respective holding companies and their customs clearance agents) to:</p>
        
        <p class="indent">1) act as our authorized courier and/or agent to do all necessary acts on our behalf for customs clearance including filing of documents, declarations and Shipping Bill for clearance of all export shipments shipped by us from time to time through the courier, express or formal customs clearance mode</p>
        
        <p class="indent">2) file documents for export customs clearance of shipments based on the declaration and information regarding the shipments provided to Sairaj by us or in the absence of the same, the information for import shipments provided to Sairaj by the consignors for delivery in India.</p>

        <p>This authorization shall remain valid until revoked in writing and acknowledged by Sairaj in writing and shall cover all our shipments sent by or addressed to our various offices / branches in India. This authorization may be produced and presented before any customs station in India as a formal authorization to your company for customs clearance of our</p>

        <p>Please provide the following Know Your Customer (KYC) document, as applicable.</p>
      </div>

      <!-- KYC Table -->
      <table class="kyc-table">
        <thead>
          <tr>
            <th class="col-num">#</th>
            <th class="col-cat">Category</th>
            <th class="col-docs">Documents Required</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>1</td>
            <td>Individual</td>
            <td>
              <strong>Any one of the following document</strong>
              <div class="checkbox-list">
                <div class="checkbox-item ${shipperKycType?.toLowerCase() === 'passport' ? 'checked' : ''}">
                  <span class="checkbox">${shipperKycType?.toLowerCase() === 'passport' ? '☑' : '☐'}</span> Passport Copy with address page
                </div>
                <div class="checkbox-item ${shipperKycType?.toLowerCase() === 'aadhaar' || shipperKycType?.toLowerCase() === 'aadhar' ? 'checked' : ''}">
                  <span class="checkbox">${shipperKycType?.toLowerCase() === 'aadhaar' || shipperKycType?.toLowerCase() === 'aadhar' ? '☑' : '☐'}</span> Aadhaar Card
                </div>
                <div class="checkbox-item ${shipperKycType?.toLowerCase() === 'pan' ? 'checked' : ''}">
                  <span class="checkbox">${shipperKycType?.toLowerCase() === 'pan' ? '☑' : '☐'}</span> PAN Card
                </div>
                <div class="checkbox-item ${shipperKycType?.toLowerCase() === 'voter' || shipperKycType?.toLowerCase() === 'voterid' ? 'checked' : ''}">
                  <span class="checkbox">${shipperKycType?.toLowerCase() === 'voter' || shipperKycType?.toLowerCase() === 'voterid' ? '☑' : '☐'}</span> Voter ID Card
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <td>2</td>
            <td>Firms, company, institution registered Under GST Laws</td>
            <td>
              <div class="checkbox-list">
                <div class="checkbox-item ${shipperKycType?.toLowerCase() === 'gstin' || shipperKycType?.toLowerCase() === 'gst' ? 'checked' : ''}">
                  <span class="checkbox">${shipperKycType?.toLowerCase() === 'gstin' || shipperKycType?.toLowerCase() === 'gst' ? '☑' : '☐'}</span> GSTIN registration copy
                </div>
                <div class="checkbox-item ${shipperKycType?.toLowerCase() === 'iec' ? 'checked' : ''}">
                  <span class="checkbox">${shipperKycType?.toLowerCase() === 'iec' ? '☑' : '☐'}</span> IEC Number
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <td>3</td>
            <td>Exempted/ Non Registered firms, company, institution under GST Laws</td>
            <td>
              <div class="checkbox-list">
                <div class="checkbox-item">
                  <span class="checkbox">☐</span> PAN Card
                </div>
                <div class="checkbox-item">
                  <span class="checkbox">☐</span> IEC Number
                </div>
              </div>
            </td>
          </tr>
          <tr>
            <td>4</td>
            <td>Embassy/ U.N. Bodies/ Government entities</td>
            <td>
              <div class="checkbox-list">
                <div class="checkbox-item">
                  <span class="checkbox">☐</span> Unique Identification Number (UIN) copy
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- IEC Note -->
      <div class="iec-note">
        <p>IEC number is mandatory to file shipment for clearance. The name / branch address of company as on the invoice should match as with that on shipping documents accompanying the shipment</p>
      </div>

      <!-- Declaration -->
      <div class="declaration">
        <p>We/ I duly declare that the above document is the true copy and verifiable with original KYC document if called upon by customs / government authorities.</p>
      </div>

      <!-- Signature Section -->
      <div class="signature-section">
        <div class="signature-left">
          <p><strong>Name : </strong>${shipperCompany || shipperName}</p>
          ${shipperKycType && shipperKycNumber ? `<p><strong>${shipperKycType.toUpperCase()} : </strong>${shipperKycNumber}</p>` : ''}
        </div>
        <div class="signature-right">
          <p>Sign/Stamped By Authorized Signatory</p>
          <div class="signature-box"></div>
        </div>
      </div>
    </div>
  `;
};

/**
 * Get complete Authorization Letter document HTML
 */
const getAuthorizationLetterDocument = (awbData: AWBDetail, countriesMap?: CountryLookup): string => {
  const letterHTML = generateAuthorizationLetterHTML(awbData, countriesMap);

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Authorization Letter - ${awbData.awbNumber}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    @page {
      size: A4;
      margin: 8mm;
    }

    body {
      font-family: 'Times New Roman', Times, serif;
      font-size: 10pt;
      line-height: 1.3;
      color: #000;
      background: #f5f5f5;
      padding: 10px;
    }

    .auth-letter-page {
      width: 210mm;
      height: 297mm;
      max-height: 297mm;
      margin: 0 auto;
      padding: 10mm 12mm;
      background: white;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .letter-date {
      text-align: left;
      margin-bottom: 8px;
      font-size: 10pt;
    }

    .addressee {
      margin-bottom: 10px;
      line-height: 1.35;
    }

    .addressee p {
      margin: 0;
      font-size: 10pt;
    }

    .letter-title {
      text-align: center;
      font-size: 12pt;
      font-weight: bold;
      text-decoration: underline;
      margin: 10px 0 5px 0;
    }

    .awb-number {
      text-align: center;
      font-size: 10pt;
      margin-bottom: 8px;
    }

    .auth-text {
      margin-bottom: 8px;
      text-align: justify;
    }

    .auth-text p {
      margin-bottom: 6px;
      font-size: 9.5pt;
      line-height: 1.35;
    }

    .auth-text p.indent {
      text-indent: 25px;
    }

    /* KYC Table */
    .kyc-table {
      width: 100%;
      border-collapse: collapse;
      margin: 8px 0;
      font-size: 9pt;
    }

    .kyc-table th,
    .kyc-table td {
      border: 1px solid #000;
      padding: 4px 6px;
      vertical-align: top;
    }

    .kyc-table th {
      background: #f0f0f0;
      font-weight: bold;
      text-align: center;
    }

    .col-num {
      width: 5%;
      text-align: center;
    }

    .col-cat {
      width: 38%;
    }

    .col-docs {
      width: 57%;
    }

    .checkbox-list {
      margin-top: 2px;
    }

    .checkbox-item {
      margin: 2px 0;
      display: flex;
      align-items: flex-start;
      gap: 3px;
      font-size: 9pt;
    }

    .checkbox {
      font-family: 'Segoe UI Symbol', Arial;
      font-size: 10pt;
    }

    .checkbox-item.checked {
      font-weight: bold;
    }

    /* IEC Note */
    .iec-note {
      margin: 6px 0;
      font-size: 9pt;
      text-align: justify;
    }

    /* Declaration */
    .declaration {
      margin: 8px 0;
      font-size: 9.5pt;
      text-align: justify;
    }

    /* Signature Section */
    .signature-section {
      display: flex;
      justify-content: space-between;
      margin-top: auto;
      padding-top: 10px;
    }

    .signature-left {
      flex: 1;
    }

    .signature-left p {
      margin-bottom: 3px;
      font-size: 9.5pt;
    }

    .signature-right {
      text-align: center;
      min-width: 180px;
    }

    .signature-right p {
      font-size: 9.5pt;
      margin-bottom: 5px;
    }

    .signature-box {
      width: 160px;
      height: 40px;
      border: 1px solid #999;
      margin: 0 auto;
    }

    /* Print Controls */
    .print-controls {
      position: fixed;
      top: 10px;
      right: 10px;
      z-index: 1000;
      background: #fff;
      padding: 10px;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    }

    .print-btn {
      background: #2563eb;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      margin-right: 10px;
    }

    .print-btn:hover {
      background: #1d4ed8;
    }

    .close-btn {
      background: #6b7280;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
    }

    .close-btn:hover {
      background: #4b5563;
    }

    @media print {
      body {
        padding: 0;
        background: white;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .auth-letter-page {
        box-shadow: none;
        padding: 8mm 10mm;
        width: 100%;
        height: 100vh;
        max-height: 100vh;
        overflow: hidden;
        page-break-inside: avoid;
      }

      .print-controls {
        display: none !important;
      }
    }
  </style>
</head>
<body>
  <div class="print-controls">
    <button class="print-btn" onclick="window.print()">🖨️ Print / Save as PDF</button>
    <button class="close-btn" onclick="window.close()">✕ Close</button>
  </div>
  ${letterHTML}
</body>
</html>
  `;
};

/**
 * Open Authorization Letter in new window for printing/downloading
 */
export const openAuthorizationLetterPrint = (awbData: AWBDetail, countriesMap?: CountryLookup): void => {
  const htmlContent = getAuthorizationLetterDocument(awbData, countriesMap);
  const printWindow = window.open('', '_blank');

  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  }
};

// ============================================================================
// SHIPPER & ACCOUNTS COPY GENERATION
// ============================================================================

/**
 * Format date for Shipper Copy (DD/MM/YYYY)
 */
const formatShipperCopyDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB');
  } catch {
    return '';
  }
};

/**
 * Generate barcode SVG for AWB number (larger format)
 */
const generateAWBBarcodeSVG = (text: string): string => {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  try {
    JsBarcode(svg, text, {
      format: 'CODE128',
      width: 2,
      height: 45,
      displayValue: false,
      margin: 0,
    });
    return svg.outerHTML;
  } catch (error) {
    console.error('Error generating AWB barcode:', error);
    return '';
  }
};

/**
 * Get customer/account name from AWB data
 */
const getAccountName = (awbData: AWBDetail): string => {
  if (typeof awbData.customer === 'object' && awbData.customer) {
    return (awbData.customer as any).name || (awbData.customer as any).companyName || '';
  }
  return '';
};

/**
 * Get customer/account number from AWB data
 */
const getAccountNumber = (awbData: AWBDetail): string => {
  if (typeof awbData.customer === 'object' && awbData.customer) {
    return (awbData.customer as any).accountNumber || (awbData.customer as any).customerCode || '';
  }
  return '';
};

/**
 * Generate single page of Shipper/Accounts Copy
 */
const generateShipperCopyPageHTML = (awbData: AWBDetail, copyType: 'ACCOUNTS COPY' | 'SHIPPER COPY', countriesMap?: CountryLookup): string => {
  const awbBarcode = generateAWBBarcodeSVG(awbData.awbNumber);
  
  // Get origin/destination country codes
  const originCode = getCountryName(awbData.origin, countriesMap) || 
    getCountryName(awbData.shipper?.country, countriesMap) || 'IN';
  const destCode = getCountryName(awbData.destination, countriesMap) || 
    getCountryName(awbData.consignee?.country, countriesMap) || '';
  
  // Get shipper details
  const shipperCompany = awbData.shipper?.company || '';
  const shipperName = awbData.shipper?.personName || '';
  const shipperAddress = [
    awbData.shipper?.address1,
    awbData.shipper?.address2,
    awbData.shipper?.address3
  ].filter(Boolean).join(', ');
  const shipperCity = awbData.shipper?.city || '';
  const shipperState = awbData.shipper?.stateCounty || '';
  const shipperCountry = getCountryName(awbData.shipper?.country, countriesMap) || '';
  const shipperPincode = awbData.shipper?.postZipCode || '';
  const shipperPhone = awbData.shipper?.phoneNumber || '';
  const shipperPhone2 = awbData.shipper?.mobileNumber || '';
  
  // Get consignee details
  const consigneeCompany = awbData.consignee?.company || '';
  const consigneeName = awbData.consignee?.personName || '';
  const consigneeAddress = [
    awbData.consignee?.address1,
    awbData.consignee?.address2,
    awbData.consignee?.address3
  ].filter(Boolean).join(', ');
  const consigneeCity = awbData.consignee?.city || '';
  const consigneeState = awbData.consignee?.stateCounty || '';
  const consigneeCountry = getCountryName(awbData.consignee?.country, countriesMap) || '';
  const consigneePincode = awbData.consignee?.postZipCode || '';
  const consigneePhone = awbData.consignee?.phoneNumber || '';
  const consigneePhone2 = awbData.consignee?.mobileNumber || '';
  
  // Get shipment details
  const bookingDate = formatShipperCopyDate(awbData.bookingDate);
  const description = awbData.shipmentInvoice?.lineItems?.map(item => item.description).filter(Boolean).join(', ') || 
    awbData.contentDescription || '';
  const shipValue = awbData.shipmentInvoice?.totalValue || awbData.declaredValue || 0;
  const currency = getCurrencyCode(awbData.shipmentInvoice?.currency || 'INR');
  const productType = getProductTypeName(awbData.product) || 'NON-DOX';
  const serviceName = getServiceName(awbData.service) || '';
  const forwardingNo = awbData.forwardingNumber || '';
  const refName = awbData.referenceName || '';
  const refNo = awbData.referenceNumber || '';
  
  // Get weights
  const chargeableWeight = awbData.totalChargeableWeight || awbData.totalActualWeight || 0;
  const actualWeight = awbData.totalActualWeight || 0;
  const volumetricWeight = awbData.totalVolumetricWeight || 0;
  const pieces = awbData.totalPieces || 1;
  
  // Get dimensions
  const dimensions = awbData.weightDimensions && awbData.weightDimensions.length > 0
    ? `${awbData.weightDimensions[0].length || 0}*${awbData.weightDimensions[0].breadth || 0}*${awbData.weightDimensions[0].height || 0}`
    : '';
  
  // Get charges - these would come from sales billing
  const freight = 0; // Placeholder
  const otherCharges = 0;
  const fsc = 0;
  const cgst = 0;
  const sgst = 0;
  const igst = 0;
  const total = 0;
  
  // Get shipper KYC
  const shipperGSTIN = awbData.shipper?.kycType?.toLowerCase() === 'gstin' || awbData.shipper?.kycType?.toLowerCase() === 'gst' 
    ? awbData.shipper?.kycNumber || '' 
    : '';
  
  // Get invoice details
  const invoiceNo = awbData.invoiceNumber || awbData.awbNumber;
  const ewayBillNo = awbData.ewayBillNumber || '';
  
  // Account info
  const accountNumber = getAccountNumber(awbData);
  const accountName = getAccountName(awbData);

  return `
    <div class="shipper-copy-page">
      <!-- Header -->
      <div class="header-section">
        <div class="logo-area">
          <div class="logo-placeholder">
            <span class="logo-text">SAIRAJ</span>
            <span class="logo-subtext">INTERNATIONAL</span>
          </div>
        </div>
        <div class="company-details">
          <h1 class="company-name">SAIRAJ INTERNATIONAL</h1>
          <p>GR. FLOOR, D. C. SILK MILLS BUSINESS PARK, CHUNAWALA ESTATE, KONDIVITA ROAD,</p>
          <p>OPP. P. D. HINDUJA COLLEGE OF NURSING, ANDHERI...</p>
          <p><strong>Phone :</strong> 9594202250/ 8828423991/ 8828423993/ 8828423994</p>
          <p><strong>Email :</strong> INFO.SAIRAJ2@GMAIL.COM</p>
          <p><strong>Website :</strong> WWW.SAIRAJINTERNATIONAL.IN</p>
        </div>
        <div class="awb-section">
          <div class="awb-label">AWB NUMBER</div>
          <div class="awb-number">${awbData.awbNumber}</div>
          <div class="awb-barcode">${awbBarcode}</div>
        </div>
      </div>

      <!-- Account Info Row -->
      <table class="info-table">
        <tr class="header-row">
          <th style="width: 25%">ACCOUNT NUMBER - NAME</th>
          <th style="width: 12%">ORIGIN</th>
          <th style="width: 20%">DESTINATION</th>
          <th style="width: 15%">CHARGEABLE WT.</th>
          <th style="width: 14%">Actual Wt - ${actualWeight.toFixed(2)}</th>
          <th style="width: 14%">PCS - ${pieces}</th>
        </tr>
        <tr>
          <td>${accountNumber}${accountNumber && accountName ? ' - ' : ''}${accountName}</td>
          <td>${originCode.substring(0, 2).toUpperCase()}</td>
          <td>${destCode.substring(0, 2).toUpperCase()}</td>
          <td>${chargeableWeight.toFixed(2)}</td>
          <td>Volumetric Wt - ${volumetricWeight.toFixed(2)}</td>
          <td>${dimensions}</td>
        </tr>
      </table>

      <!-- Shipper / Consignee Details -->
      <table class="address-table">
        <tr>
          <td class="shipper-col">
            <div class="address-block">
              <div class="address-row">
                <span class="label">Sender's Company -</span>
                <span class="value">${shipperCompany.toUpperCase()}</span>
              </div>
              <div class="address-row">
                <span class="label">Sender's Name-</span>
                <span class="value">${shipperName.toUpperCase()}</span>
              </div>
              <div class="address-row">
                <span class="label">ADDRESS-</span>
                <span class="value">${shipperAddress.toUpperCase()}</span>
              </div>
              <div class="address-row">
                <span class="value">${shipperCity.toUpperCase()}${shipperState ? ', ' + shipperState.toUpperCase() : ''}</span>
              </div>
              <div class="address-row">
                <span class="value">${shipperCountry.toUpperCase()}${shipperPincode ? ', ' + shipperPincode : ''}</span>
              </div>
              <div class="address-row">
                <span class="label">TEL NO1.</span>
                <span class="value">${shipperPhone}</span>
              </div>
            </div>
          </td>
          <td class="consignee-col">
            <div class="address-block">
              <div class="address-row">
                <span class="label">Recipient's Company-</span>
                <span class="value">${consigneeCompany.toUpperCase()}</span>
              </div>
              <div class="address-row">
                <span class="label">Recipient's Name-</span>
                <span class="value">${consigneeName.toUpperCase()}</span>
              </div>
              <div class="address-row">
                <span class="label">ADDRESS-</span>
                <span class="value">${consigneeAddress.toUpperCase()}</span>
              </div>
              <div class="address-row">
                <span class="value">${consigneeCity.toUpperCase()}${consigneeState ? ', ' + consigneeState.toUpperCase() : ''}</span>
              </div>
              <div class="address-row">
                <span class="value">${consigneeCountry.toUpperCase()}${consigneePincode ? ', ' + consigneePincode : ''}</span>
              </div>
              <div class="address-row">
                <span class="label">TEL NO1.</span>
                <span class="value">${consigneePhone}</span>
                ${consigneePhone2 ? `<span class="label" style="margin-left: 20px;">TEL NO2.</span><span class="value">${consigneePhone2}</span>` : ''}
              </div>
            </div>
          </td>
        </tr>
      </table>

      <!-- Shipment Details Section -->
      <table class="details-table">
        <tr>
          <td class="col-booking" rowspan="2">
            <div class="booking-section">
              <div class="booking-label">Booking Date</div>
              <div class="booking-value">${bookingDate}</div>
            </div>
            <div class="shipper-agreement">
              <strong>SHIPPER AGREEMENT</strong><br>
              Shipper agrees to SAIRAJ INTERNATIONAL standard terms and conditions of carriage<br>
              <strong>SHIPPER'S SIGN</strong>
            </div>
            <div class="date-section">
              <strong>DATE</strong>
            </div>
          </td>
          <td class="col-service">
            <div class="detail-cell">
              <div class="detail-header">DESCRIPTION OF GOODS</div>
              <div class="detail-value description-text">${description.toUpperCase()}</div>
            </div>
          </td>
          <td class="col-center">
            <div class="detail-cell">
              <div class="detail-header">Ship Value</div>
              <div class="detail-value">${shipValue.toFixed(2)} ${currency}</div>
            </div>
          </td>
          <td class="col-type">
            <div class="detail-cell">
              <div class="detail-header">INTERNATIONAL</div>
              <div class="detail-value">${productType.toUpperCase()}</div>
            </div>
          </td>
          <td class="col-charges" rowspan="2">
            <div class="charges-section">
              <div class="charge-row"><span>Freight:</span> <span>${freight.toFixed(2)}</span></div>
              <div class="charge-row"><span>Other Charges:</span> <span>${otherCharges.toFixed(2)}</span></div>
              <div class="charge-row"><span>FSC:</span> <span>${fsc.toFixed(2)}</span></div>
              <div class="charge-row"><span>CGST @ 9%:</span> <span>${cgst.toFixed(2)}</span></div>
              <div class="charge-row"><span>SGST @ 9%:</span> <span>${sgst.toFixed(2)}</span></div>
              <div class="charge-row"><span>IGST @ 18%:</span> <span>${igst.toFixed(2)}</span></div>
              <div class="charge-row total"><span>TOTAL:</span> <span>${total.toFixed(2)}</span></div>
              <div class="copy-type">${copyType}</div>
            </div>
          </td>
        </tr>
        <tr>
          <td class="col-service">
            <div class="service-grid">
              <div class="service-cell">
                <div class="detail-header">SERVICE</div>
                <div class="detail-value">${serviceName.toUpperCase()}</div>
              </div>
              <div class="service-cell">
                <div class="detail-header">FORWARDING NO.</div>
                <div class="detail-value">${forwardingNo}</div>
              </div>
            </div>
            <div class="condition-text">Received in good order & condition</div>
            <div class="sign-section">
              <div><strong>NAME</strong></div>
              <div><strong>SIGN</strong></div>
            </div>
          </td>
          <td class="col-center" colspan="2">
            <div class="ref-grid">
              <div class="ref-cell">
                <div class="detail-header">Ref Name</div>
                <div class="detail-value">${refName}</div>
              </div>
              <div class="ref-cell">
                <div class="detail-header">Ref No.</div>
                <div class="detail-value">${refNo}</div>
              </div>
            </div>
            <div class="gstin-section">
              ${shipperGSTIN ? `<div><strong>GSTIN :</strong> ${shipperGSTIN}</div>` : ''}
              <div>All Cheques/DD should be drawn in favour of SAIRAJ INTERNATIONAL</div>
            </div>
            <div class="invoice-section">
              <div><strong>INVOICE NO:</strong> ${invoiceNo}</div>
              <div><strong>EWAY BILL NO:</strong> ${ewayBillNo}</div>
            </div>
          </td>
        </tr>
      </table>
    </div>
  `;
};

/**
 * Generate Terms and Conditions section
 */
const generateTermsAndConditionsHTML = (): string => {
  return `
    <div class="terms-section">
      <h3 class="terms-title">GENERAL TERMS & CONDITIONS FOR SHIPMENT CARRIED BY/FOR SAIRAJ INTERNATIONAL</h3>
      <p class="terms-header">Head Office: Mumbai. Ph:9594202250/ 8828423991/ 8828423993/ 8828423994, Email: INFO.SAIRAJ2@GMAIL.COM</p>
      <p class="declaration"><strong><u>DECLARATION BY THE CONSIGNOR / SHIPPER :</u></strong> I/We hereby declare that this consignment does not contain any item expressly prohibited locals,state government,central government,post office,rail,ship & airport authorities. The following terms & conditions are acceptable to me/us.</p>
      
      <ol class="terms-list">
        <li>IF THE CONSIGNMENT IS LOST OR DAMAGED IN TRANSIT BY '. SAIRAJ INTERNATIONAL .' ,THE MAXIMUM CLAIM PAYABLE BY '. SAIRAJ INTERNATIONAL .' WILL BE RS.100/- PER KG OR RS.5,000.00 WHICHEVER IS LOWER.</li>
        <li>'. SAIRAJ INTERNATIONAL .' WILL NOT BE RESPONSIBLE FOR DELAYED DELIVERY DUE TO FIRE,ACCIDENTS,RIOTS,STRIKES & NATURAL CALAMITIES,FLIGHT DELAY,OFFLOADING OF CARGO,AIRLINES SPACE PROBLEM,AIRLINES BAGGING MIS -ROUTING, CUSTOMS PROCESS DELAY, LOCKDOWN, COVID-19 SITUATIONS & RESTRICTIONS, WAR, CIVIL WAR , DISTURBED AREA, IN VIEW OF THE COVID 19 LOCKDOWN, FORCE MAJEURE CONDITIONS EXIST AND '. SAIRAJ INTERNATIONAL .' IS NOT RESPONSIBLE FOR DELAYS OR NON DELIVERY.</li>
        <li>NO CLAIM WOULD BE ACCEPTED AFTER 48HOURS OF DELIVERY .</li>
        <li>THE CONSIGNOR IS RESPONSIBLE FOR ALL THE CONSEQUENCES ,IF ANY INCORRECT/FALSE DECLARATION IS GIVEN.</li>
        <li>ANY DISPUTE,CONTROVERSY OF CLAIM ARISING OUT OF THIS SHIPMENTS SHALL BE SUBJECT TO THE JURISDICTION OF COURTS OF VADODARA ONLY IRRESPECTIVE OF THE PLACE OF ORIGIN,PAYMENT AND DESTINATION OF CONSIGNMENT.</li>
        <li>THE CONSIGNMENTS HANDED OVER TO '. SAIRAJ INTERNATIONAL .' DOES NOT VIOLATE THE RULES AND REGULATIONS OF POST & TELEGRAPH DEPTT, ANS IATA UNDER GENEVA CONVENTION.</li>
        <li>NO DELIVERY TIME IS GIVEN OR COMMITED FOR SHIPMENTS BOOKED FOR ECONOMY SERVICES.</li>
        <li>'. SAIRAJ INTERNATIONAL .' IS NOT RESPONSIBLE FOR ANY DUTIES/PENALTIES,CHARGES APPLIED TO ANY SHIPMENT BY ANY GOVERNMENT AUTHORITIES OF DESTINATION COUNTRY.</li>
        <li>CLAIMS FOR LOST SHIPMENT WILL BE SETTLED ONLY BETWEEN THE ORIGINAL CONSIGNOR AND OUR COMPANY WITHIN A MINIMUM PERIOD OF 30 TO 45DAYS STARTING FROM THE DATE OF CONFIRMATION OF LOSS/LOST OF THE SHIPMENT, SUBJECT TO VERIFICATION OF THE ORIGINAL INVOICES SUBMITED AT THE TIME OF BOOKING AND AT THE TIME OF SETTLEMENT , WITH PROPER PAPERWORKS IN ORDER.</li>
        <li>THE ABOVE POINT, THAT IS, POINT NUMBER 9, IS VALID & APPLICABLE ONLY FOR DIRECT CONTRACTUAL CLIENTS OF '. SAIRAJ INTERNATIONAL .' , AND IT IS NOT VALID, IF THE SHIPMENT IS BOOKED BY/THROUGH ANY SUB-AGENT, FRANCHISEE, COLLECTION CENTER, WORKING ON BEHALF OF '. SAIRAJ INTERNATIONAL .' BUT NOR BEING THE '. SAIRAJ INTERNATIONAL .' DIRECT HUB.</li>
        <li>IN NO CASE REFUND AMOUNT WILL BE PAID BACK TO CONSIGNOR IN CASH OR CHEQUE, FREIGHT/COURIER CHARGES WILL BE ADJUSTED AGAINST FREE SHIPPING OF SECOND PARCEL OF SAME WEIGHT THROUGH SAME SERVICE TO SAME COUNTRY AND ADDRESS.VALUE AMOUNT OF CONTENTS LOST ONCE DECIDED AT THE TIME OF CLAIM SETTLEMENT WILL BE REFUNDED AS AND AGAINST FREIGHT/COURIER CHARGES OF NEXT SHIPMENT/PARCEL AND NOT IN CASH.</li>
        <li>KINDLY CHECK THE AUTHORIZATION VALIDITY OF THE PERSON,PLACE, WHERE THE SHIPMENTS IS BEING BOOKED, SHIPMENT SHOULD BE BOOKED ONLY AT THE VALID BOOKING FACILITY, PRE-CHECK THE AGENT/FRANCHISEE TYPE THE TIME OF BOOKING IS ADVISABLE.</li>
        <li>STATUS OF THE SHIPMENT WILL BE CONSIDERED " AS VALID " ONLY AFTER THE SHIPMENT HAS REACHED '. SAIRAJ INTERNATIONAL .' HUB, SHIPMENT WILL BE CONSIDERED IN-VALID UNTIL IT HAS REACHED '. SAIRAJ INTERNATIONAL .' HUB AND THE IN-SCAN PROCESS HAS BEEN DONE BY '. SAIRAJ INTERNATIONAL .' HUB, SHIPMENTS NOT ACCEPTED BY '. SAIRAJ INTERNATIONAL .' OR NOT BEING HANDED OVER TO '. SAIRAJ INTERNATIONAL .' HUB WILL BE CONSIDERED AS NOT IN RECORDS, EVEN THOUGH IT HAS BEEN BOOKED BY ANY OTHER AGENT, FRANCHISEE , COLLECTION CENTER, ETC. , IRRESPECTIVE OF THEIR TYPE.</li>
      </ol>
      
      <p class="final-declaration"><strong><u>THE SHIPPER HAD READ,UNDERSTOOD AND AGREES TO THE STANDARD TERMS AND CONDITIONS OF THE CARRIAGE.</u></strong></p>
      
      <div class="signature-area">
        <span class="signature-label">Signature</span>
      </div>
    </div>
  `;
};

/**
 * Generate compact half-page version for Page 1
 */
const generateCompactCopyHTML = (awbData: AWBDetail, copyType: 'ACCOUNTS COPY' | 'SHIPPER COPY', countriesMap?: CountryLookup): string => {
  const awbBarcode = generateAWBBarcodeSVG(awbData.awbNumber);
  
  // Get all data
  const originCode = getCountryName(awbData.origin, countriesMap) || getCountryName(awbData.shipper?.country, countriesMap) || 'IN';
  const destCode = getCountryName(awbData.destination, countriesMap) || getCountryName(awbData.consignee?.country, countriesMap) || '';
  const shipperCompany = awbData.shipper?.company || '';
  const shipperName = awbData.shipper?.personName || '';
  const shipperAddress = [awbData.shipper?.address1, awbData.shipper?.address2].filter(Boolean).join(', ');
  const shipperCity = awbData.shipper?.city || '';
  const shipperState = awbData.shipper?.stateCounty || '';
  const shipperCountry = getCountryName(awbData.shipper?.country, countriesMap) || '';
  const shipperPincode = awbData.shipper?.postZipCode || '';
  const shipperPhone = awbData.shipper?.phoneNumber || '';
  const consigneeCompany = awbData.consignee?.company || '';
  const consigneeName = awbData.consignee?.personName || '';
  const consigneeAddress = [awbData.consignee?.address1, awbData.consignee?.address2].filter(Boolean).join(', ');
  const consigneeCity = awbData.consignee?.city || '';
  const consigneeState = awbData.consignee?.stateCounty || '';
  const consigneeCountry = getCountryName(awbData.consignee?.country, countriesMap) || '';
  const consigneePincode = awbData.consignee?.postZipCode || '';
  const consigneePhone = awbData.consignee?.phoneNumber || '';
  const bookingDate = formatShipperCopyDate(awbData.bookingDate);
  const description = awbData.shipmentInvoice?.lineItems?.map(item => item.description).filter(Boolean).join(', ') || awbData.contentDescription || '';
  const shipValue = awbData.shipmentInvoice?.totalValue || awbData.declaredValue || 0;
  const currency = getCurrencyCode(awbData.shipmentInvoice?.currency || 'INR');
  const productType = getProductTypeName(awbData.product) || 'NON-DOX';
  const serviceName = getServiceName(awbData.service) || '';
  const chargeableWeight = awbData.totalChargeableWeight || awbData.totalActualWeight || 0;
  const actualWeight = awbData.totalActualWeight || 0;
  const volumetricWeight = awbData.totalVolumetricWeight || 0;
  const pieces = awbData.totalPieces || 1;
  const dimensions = awbData.weightDimensions?.[0] ? `${awbData.weightDimensions[0].length || 0}*${awbData.weightDimensions[0].breadth || 0}*${awbData.weightDimensions[0].height || 0}` : '';
  const shipperGSTIN = (awbData.shipper?.kycType?.toLowerCase() === 'gstin' || awbData.shipper?.kycType?.toLowerCase() === 'gst') ? awbData.shipper?.kycNumber || '' : '';
  const invoiceNo = awbData.invoiceNumber || awbData.awbNumber;
  const ewayBillNo = awbData.ewayBillNumber || '';
  const accountNumber = getAccountNumber(awbData);
  const accountName = getAccountName(awbData);

  return `
    <div class="compact-copy">
      <!-- Header -->
      <div class="compact-header">
        <div class="compact-logo">
          <span class="logo-text">SAIRAJ</span>
          <span class="logo-sub">INTERNATIONAL</span>
        </div>
        <div class="compact-company">
          <strong>SAIRAJ INTERNATIONAL</strong>
          <span>GR. FLOOR, D.C. SILK MILLS BUSINESS PARK, CHUNAWALA ESTATE, KONDIVITA ROAD,</span>
          <span>OPP. P.D. HINDUJA COLLEGE OF NURSING, ANDHERI...</span>
          <span>Phone: 9594202250/ 8828423991/ 8828423993/ 8828423994 | Email: INFO.SAIRAJ2@GMAIL.COM</span>
        </div>
        <div class="compact-awb">
          <div class="awb-label">AWB NUMBER</div>
          <div class="awb-num">${awbData.awbNumber}</div>
          <div class="awb-barcode">${awbBarcode}</div>
        </div>
      </div>

      <!-- Info Row -->
      <table class="compact-info">
        <tr>
          <th>ACCOUNT NUMBER - NAME</th>
          <th>ORIGIN</th>
          <th>DESTINATION</th>
          <th>CHARGEABLE WT.</th>
          <th>Actual Wt - ${actualWeight.toFixed(2)}</th>
          <th>PCS - ${pieces}</th>
        </tr>
        <tr>
          <td>${accountNumber}${accountNumber && accountName ? ' - ' : ''}${accountName}</td>
          <td>${originCode.substring(0, 2).toUpperCase()}</td>
          <td>${destCode.substring(0, 2).toUpperCase()}</td>
          <td>${chargeableWeight.toFixed(2)}</td>
          <td>Vol Wt - ${volumetricWeight.toFixed(2)}</td>
          <td>${dimensions}</td>
        </tr>
      </table>

      <!-- Address Row -->
      <table class="compact-address">
        <tr>
          <td>
            <strong>Sender's Company-</strong> ${shipperCompany.toUpperCase()}<br>
            <strong>Sender's Name-</strong> ${shipperName.toUpperCase()}<br>
            <strong>ADDRESS-</strong> ${shipperAddress.toUpperCase()}<br>
            ${shipperCity.toUpperCase()}${shipperState ? ', ' + shipperState.toUpperCase() : ''}<br>
            ${shipperCountry.toUpperCase()}${shipperPincode ? ', ' + shipperPincode : ''}<br>
            <strong>TEL NO1.</strong> ${shipperPhone}
          </td>
          <td>
            <strong>Recipient's Company-</strong> ${consigneeCompany.toUpperCase()}<br>
            <strong>Recipient's Name-</strong> ${consigneeName.toUpperCase()}<br>
            <strong>ADDRESS-</strong> ${consigneeAddress.toUpperCase()}<br>
            ${consigneeCity.toUpperCase()}${consigneeState ? ', ' + consigneeState.toUpperCase() : ''}<br>
            ${consigneeCountry.toUpperCase()}${consigneePincode ? ', ' + consigneePincode : ''}<br>
            <strong>TEL NO1.</strong> ${consigneePhone}
          </td>
        </tr>
      </table>

      <!-- Details Row -->
      <table class="compact-details">
        <tr>
          <td class="booking-col">
            <div><strong>Booking Date</strong></div>
            <div>${bookingDate}</div>
            <div class="agreement">SHIPPER AGREEMENT<br>Shipper agrees to SAIRAJ INTERNATIONAL standard terms<br><strong>SHIPPER'S SIGN</strong></div>
            <div><strong>DATE</strong></div>
          </td>
          <td class="desc-col">
            <div class="cell-header">DESCRIPTION OF GOODS</div>
            <div class="desc-text">${description.toUpperCase().substring(0, 50)}${description.length > 50 ? '...' : ''}</div>
            <div class="service-row">
              <span><strong>SERVICE:</strong> ${serviceName.toUpperCase()}</span>
            </div>
            <div>Received in good order & condition</div>
            <div><strong>NAME</strong> _____________ <strong>SIGN</strong> _____________</div>
          </td>
          <td class="value-col">
            <div class="cell-header">Ship Value</div>
            <div>${shipValue.toFixed(2)} ${currency}</div>
            <div class="cell-header">INTERNATIONAL</div>
            <div>${productType.toUpperCase()}</div>
            ${shipperGSTIN ? `<div><strong>GSTIN:</strong> ${shipperGSTIN}</div>` : ''}
            <div><strong>INVOICE NO:</strong> ${invoiceNo}</div>
            ${ewayBillNo ? `<div><strong>EWAY BILL:</strong> ${ewayBillNo}</div>` : ''}
          </td>
          <td class="charges-col">
            <div class="charge-line">Freight: 0.00</div>
            <div class="charge-line">Other: 0.00</div>
            <div class="charge-line">FSC: 0.00</div>
            <div class="charge-line">CGST@9%: 0.00</div>
            <div class="charge-line">SGST@9%: 0.00</div>
            <div class="charge-line">IGST@18%: 0.00</div>
            <div class="total-line"><strong>TOTAL: 0.00</strong></div>
            <div class="copy-label">${copyType}</div>
          </td>
        </tr>
      </table>
    </div>
  `;
};

/**
 * Get complete Shipper & Accounts Copy document HTML
 */
const getShipperAccountsCopyDocument = (awbData: AWBDetail, countriesMap?: CountryLookup): string => {
  // Page 1: Two compact copies stacked
  const accountsCompact = generateCompactCopyHTML(awbData, 'ACCOUNTS COPY', countriesMap);
  const shipperCompact = generateCompactCopyHTML(awbData, 'SHIPPER COPY', countriesMap);
  
  // Page 2: Full shipper copy with terms
  const shipperFull = generateShipperCopyPageHTML(awbData, 'SHIPPER COPY', countriesMap);
  const termsContent = generateTermsAndConditionsHTML();

  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Shipper & Accounts Copy - ${awbData.awbNumber}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }

    @page {
      size: A4;
      margin: 4mm;
    }

    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 9px;
      line-height: 1.25;
      color: #000;
      background: #f5f5f5;
      padding: 10px;
    }

    /* ==================== PAGE 1: COMPACT COPIES ==================== */
    .page-one {
      width: 210mm;
      height: 297mm;
      margin: 0 auto 20px auto;
      padding: 4mm;
      background: white;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      page-break-after: always;
      display: flex;
      flex-direction: column;
      gap: 3mm;
    }

    .compact-copy {
      flex: 1;
      border: 2px solid #000;
      display: flex;
      flex-direction: column;
    }

    .compact-header {
      display: flex;
      border-bottom: 1px solid #000;
      min-height: 50px;
    }

    .compact-logo {
      width: 70px;
      padding: 4px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      border-right: 1px solid #000;
      color: #008080;
    }

    .compact-logo .logo-text {
      font-size: 14px;
      font-weight: bold;
      font-style: italic;
    }

    .compact-logo .logo-sub {
      font-size: 6px;
      font-weight: bold;
    }

    .compact-company {
      flex: 1;
      padding: 4px 8px;
      font-size: 7px;
      line-height: 1.3;
    }

    .compact-company strong {
      font-size: 12px;
      display: block;
      margin-bottom: 2px;
    }

    .compact-company span {
      display: block;
    }

    .compact-awb {
      width: 110px;
      padding: 4px;
      border-left: 1px solid #000;
      text-align: center;
    }

    .compact-awb .awb-label {
      font-size: 8px;
      font-weight: bold;
    }

    .compact-awb .awb-num {
      font-size: 14px;
      font-weight: bold;
      margin: 2px 0;
    }

    .compact-awb .awb-barcode svg {
      width: 100px;
      height: 30px;
    }

    .compact-info {
      width: 100%;
      border-collapse: collapse;
      border-bottom: 1px solid #000;
    }

    .compact-info th, .compact-info td {
      border: 1px solid #000;
      border-top: none;
      padding: 2px 4px;
      text-align: center;
      font-size: 7px;
    }

    .compact-info th {
      background: #f0f0f0;
      font-weight: bold;
      font-size: 6px;
    }

    .compact-address {
      width: 100%;
      border-collapse: collapse;
      border-bottom: 1px solid #000;
    }

    .compact-address td {
      width: 50%;
      border: 1px solid #000;
      border-top: none;
      padding: 4px 6px;
      font-size: 7px;
      line-height: 1.35;
      vertical-align: top;
    }

    .compact-details {
      width: 100%;
      border-collapse: collapse;
      flex: 1;
    }

    .compact-details td {
      border: 1px solid #000;
      border-top: none;
      padding: 4px;
      vertical-align: top;
      font-size: 7px;
    }

    .compact-details .booking-col { width: 15%; }
    .compact-details .desc-col { width: 35%; }
    .compact-details .value-col { width: 25%; }
    .compact-details .charges-col { width: 25%; }

    .compact-details .cell-header {
      background: #e0e0e0;
      font-weight: bold;
      text-align: center;
      padding: 2px;
      margin: -4px -4px 3px -4px;
      font-size: 6px;
    }

    .compact-details .agreement {
      font-size: 6px;
      line-height: 1.3;
      margin: 4px 0;
    }

    .compact-details .desc-text {
      min-height: 15px;
      font-size: 7px;
    }

    .compact-details .service-row {
      margin: 3px 0;
      font-size: 7px;
    }

    .compact-details .charge-line {
      display: flex;
      justify-content: space-between;
      font-size: 6px;
    }

    .compact-details .total-line {
      border-top: 1px solid #000;
      padding-top: 2px;
      margin-top: 3px;
      font-size: 8px;
    }

    .compact-details .copy-label {
      text-align: center;
      font-weight: bold;
      font-size: 9px;
      margin-top: 4px;
      padding-top: 3px;
      border-top: 1px solid #000;
    }

    /* ==================== PAGE 2: FULL SHIPPER COPY ==================== */
    .page-two {
      width: 210mm;
      min-height: 297mm;
      margin: 0 auto;
      padding: 5mm;
      background: white;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    }

    .shipper-copy-page {
      width: 100%;
    }

    /* Header Section */
    .header-section {
      display: flex;
      border: 2px solid #000;
    }

    .logo-area {
      width: 80px;
      padding: 5px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-right: 1px solid #000;
    }

    .logo-placeholder {
      text-align: center;
      color: #008080;
    }

    .logo-placeholder .logo-text {
      display: block;
      font-size: 14px;
      font-weight: bold;
      font-style: italic;
    }

    .logo-placeholder .logo-subtext {
      display: block;
      font-size: 6px;
      font-weight: bold;
    }

    .company-details {
      flex: 1;
      padding: 5px 8px;
    }

    .company-name {
      font-size: 14px;
      font-weight: bold;
      margin-bottom: 2px;
    }

    .company-details p {
      font-size: 7px;
      line-height: 1.3;
      margin: 0;
    }

    .awb-section {
      width: 120px;
      padding: 5px;
      border-left: 1px solid #000;
      text-align: center;
    }

    .awb-section .awb-label {
      font-size: 8px;
      font-weight: bold;
    }

    .awb-section .awb-number {
      font-size: 16px;
      font-weight: bold;
      margin: 2px 0;
    }

    .awb-section .awb-barcode svg {
      width: 110px;
      height: 35px;
    }

    /* Info Table */
    .info-table {
      width: 100%;
      border-collapse: collapse;
      border: 2px solid #000;
      border-top: none;
    }

    .info-table th, .info-table td {
      border: 1px solid #000;
      padding: 3px 5px;
      text-align: center;
      font-size: 8px;
    }

    .info-table .header-row th {
      background: #f5f5f5;
      font-weight: bold;
      font-size: 7px;
    }

    /* Address Table */
    .address-table {
      width: 100%;
      border-collapse: collapse;
      border: 2px solid #000;
      border-top: none;
    }

    .address-table td {
      border: 1px solid #000;
      padding: 5px;
      vertical-align: top;
      width: 50%;
      font-size: 8px;
    }

    .address-block { font-size: 8px; }
    .address-row { margin-bottom: 2px; }
    .address-row .label, .address-row .value { font-weight: bold; }

    /* Details Table */
    .details-table {
      width: 100%;
      border-collapse: collapse;
      border: 2px solid #000;
      border-top: none;
    }

    .details-table td {
      border: 1px solid #000;
      padding: 4px;
      vertical-align: top;
      font-size: 8px;
    }

    .col-booking { width: 16%; }
    .col-service { width: 28%; }
    .col-center { width: 18%; }
    .col-type { width: 14%; }
    .col-charges { width: 24%; }

    .booking-section { margin-bottom: 5px; }
    .booking-label { font-size: 7px; font-weight: bold; }
    .booking-value { font-size: 9px; font-weight: bold; }
    .shipper-agreement { font-size: 6px; line-height: 1.3; margin-bottom: 8px; }
    .date-section { font-size: 7px; }

    .detail-cell { text-align: center; }
    .detail-header {
      font-size: 7px;
      font-weight: bold;
      background: #e0e0e0;
      padding: 2px;
      margin: -4px -4px 3px -4px;
    }
    .detail-value { font-size: 8px; font-weight: bold; }
    .description-text { font-size: 7px; text-align: left; min-height: 20px; }

    .service-grid, .ref-grid {
      display: flex;
      border-bottom: 1px solid #000;
      margin: -4px -4px 3px -4px;
    }

    .service-cell, .ref-cell {
      flex: 1;
      text-align: center;
      padding: 3px;
      border-right: 1px solid #000;
    }

    .service-cell:last-child, .ref-cell:last-child { border-right: none; }

    .condition-text { font-size: 7px; margin: 5px 0; }
    .sign-section { font-size: 7px; margin-top: 6px; }
    .sign-section div { margin-bottom: 8px; }

    .gstin-section { font-size: 7px; text-align: center; margin: 5px 0; line-height: 1.4; }
    .invoice-section { font-size: 7px; margin-top: 5px; }
    .invoice-section div { margin-bottom: 2px; }

    .charges-section { font-size: 7px; }
    .charge-row { display: flex; justify-content: space-between; margin-bottom: 2px; }
    .charge-row.total {
      font-weight: bold;
      font-size: 9px;
      margin-top: 5px;
      padding-top: 3px;
      border-top: 1px solid #000;
    }
    .copy-type {
      font-weight: bold;
      font-size: 10px;
      text-align: center;
      margin-top: 6px;
      padding-top: 5px;
      border-top: 1px solid #000;
    }

    /* Terms Section */
    .terms-section {
      border: 2px solid #000;
      border-top: none;
      padding: 5px;
      font-size: 6.5px;
      line-height: 1.25;
    }

    .terms-title {
      font-size: 8px;
      font-weight: bold;
      text-align: center;
      text-decoration: underline;
      margin-bottom: 2px;
    }

    .terms-header {
      font-size: 6px;
      text-align: center;
      margin-bottom: 3px;
    }

    .terms-section .declaration { margin-bottom: 3px; text-align: justify; }
    .terms-list { margin-left: 12px; margin-bottom: 3px; }
    .terms-list li { margin-bottom: 2px; text-align: justify; }
    .final-declaration { text-align: center; margin: 5px 0; }
    .signature-area { text-align: right; margin-top: 8px; }
    .signature-label { font-size: 9px; font-weight: bold; }

    /* Print Controls */
    .print-controls {
      position: fixed;
      top: 10px;
      right: 10px;
      z-index: 1000;
      background: #fff;
      padding: 10px;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    }

    .print-btn {
      background: #2563eb;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      margin-right: 10px;
    }

    .print-btn:hover { background: #1d4ed8; }

    .close-btn {
      background: #6b7280;
      color: white;
      border: none;
      padding: 10px 20px;
      border-radius: 6px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
    }

    .close-btn:hover { background: #4b5563; }

    @media print {
      body {
        padding: 0;
        background: white;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .page-one {
        box-shadow: none;
        margin: 0;
        padding: 3mm;
        height: 100vh;
        max-height: 297mm;
      }

      .page-two {
        box-shadow: none;
        margin: 0;
        padding: 3mm;
        min-height: 100vh;
        page-break-inside: avoid;
      }

      .print-controls {
        display: none !important;
      }
    }
  </style>
</head>
<body>
  <div class="print-controls">
    <button class="print-btn" onclick="window.print()">🖨️ Print / Save as PDF</button>
    <button class="close-btn" onclick="window.close()">✕ Close</button>
  </div>
  
  <!-- Page 1: Accounts Copy + Shipper Copy (stacked) -->
  <div class="page-one">
    ${accountsCompact}
    ${shipperCompact}
  </div>
  
  <!-- Page 2: Full Shipper Copy with Terms -->
  <div class="page-two">
    ${shipperFull}
    ${termsContent}
  </div>
</body>
</html>
  `;
};

/**
 * Open Shipper & Accounts Copy in new window for printing/downloading
 */
export const openShipperAccountsCopyPrint = (awbData: AWBDetail, countriesMap?: CountryLookup): void => {
  const htmlContent = getShipperAccountsCopyDocument(awbData, countriesMap);
  const printWindow = window.open('', '_blank');

  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  }
};
